import {
	ArrayInput,
	AutocompleteInput,
	BooleanInput,
	FileField,
	FileInput,
	ImageField,
	ImageInput,
	ReferenceArrayInput,
	ReferenceInput,
	required,
	SelectArrayInput,
	SelectInput,
	SimpleFormIterator,
	TextInput,
	useTranslate
} from 'react-admin';
import {
	Chip,
	Divider,
	Stack,
	Typography,
	useMediaQuery,
	useTheme
} from '@mui/material';
import {
	contactInputText,
	contactOptionText
} from '../../../../misc/ContactOption';
import { GoogleAutocompleteInput } from '../../../../misc/GoogleAutocompleteInput';
import { INPUT_SOURCE } from '../../../constants';

const PlacementSectionInputs = () => {
	const translate = useTranslate();

	return (
		<Stack gap={0}>
			<Typography variant="h6">{translate('form.fields.placement')}</Typography>
			<TextInput
				source={INPUT_SOURCE.NAME}
				label={translate('form.fields.name')}
				validate={required()}
			/>
			<GoogleAutocompleteInput isRequired={true} />
			<Stack direction="row" alignItems="center" spacing={2}>
				<TextInput
					source={INPUT_SOURCE.BLOCK_SECTION}
					label={translate('form.fields.blockSection')}
				/>
				<TextInput
					source={INPUT_SOURCE.NUMBER_OF_FLOORS}
					label={translate('form.fields.numberOfFloors')}
					validate={required()}
				/>
				<Stack>
					<BooleanInput
						sx={{
							'& .MuiFormControlLabel-label': {
								whiteSpace: 'nowrap'
							}
						}}
						source={INPUT_SOURCE.IN_OPERATION}
						label={translate('form.fields.inOperation')}
					/>
				</Stack>
			</Stack>
			<SelectInput
				source={INPUT_SOURCE.FUNCTION_TYPE}
				label={translate('form.fields.functionType')}
				choices={[
					{ id: 1, name: translate('form.fields.options.officeType') },
					{ id: 2, name: translate('form.fields.options.residentialType') },
					{
						id: 3,
						name: translate('form.fields.options.multifunctionalType')
					},
					{ id: 4, name: translate('form.fields.options.productionType') }
				]}
				validate={required()}
			/>
			<ReferenceInput source={INPUT_SOURCE.COMPLEX_ID} reference="complexes">
				<AutocompleteInput
					optionText="name"
					label={translate('form.fields.complex')}
					onCreate={() => {}}
				/>
			</ReferenceInput>
			<TextInput
				source={INPUT_SOURCE.DEVELOPER}
				label={translate('form.fields.developer')}
			/>
		</Stack>
	);
};

const DescriptionSectionInputs = () => {
	const translate = useTranslate();

	return (
		<Stack gap={0}>
			<Typography variant="h6">
				{translate('form.fields.description')}
			</Typography>
			<TextInput
				source={INPUT_SOURCE.LUN_LINK}
				label={translate('form.fields.lunLink')}
			/>
			<TextInput
				source={INPUT_SOURCE.SITE_LINK}
				label={translate('form.fields.siteLink')}
			/>
			<TextInput
				source={INPUT_SOURCE.INSTAGRAM_LINK}
				label={translate('form.fields.instagramLink')}
			/>
			<TextInput
				source={INPUT_SOURCE.MANAGEMENT_FORM}
				label={translate('form.fields.managementForm')}
				helperText={false}
			/>
		</Stack>
	);
};

const DocumentsSectionInputs = () => {
	const translate = useTranslate();

	return (
		<Stack gap={2}>
			<Typography variant="h6">Документи</Typography>
			<ImageInput
				label={translate('form.fields.images')}
				source={INPUT_SOURCE.IMAGES}
				multiple={true}
				placeholder={
					<p>Натисніть або перетягніть кілька фото, щоб завантажити</p>
				}
				helperText={false}
			>
				<ImageField source="src" title="title" />
			</ImageInput>
			<FileInput
				label={translate('form.fields.videos')}
				source={INPUT_SOURCE.VIDEOS}
				multiple={true}
				placeholder={
					<p>Натисніть або перетягніть кілька відео, щоб завантажити</p>
				}
				helperText={false}
				accept={{
					'video/*': []
				}}
			>
				<FileField source="src" title="title" />
			</FileInput>
		</Stack>
	);
};

export const ContactsSectionInputs = () => {
	const translate = useTranslate();

	return (
		<>
			<TextInput
				source={INPUT_SOURCE.COMMENTS}
				label={translate('form.fields.comments')}
				helperText={false}
				multiline={true}
				minRows={2}
			/>
			<ArrayInput
				source={INPUT_SOURCE.CONTACTS_JSONB}
				label={translate('form.fields.contacts')}
				helperText={false}
			>
				<SimpleFormIterator inline disableReordering>
					<ReferenceArrayInput
						source={INPUT_SOURCE.CONTACT_ID}
						reference="contacts_summary"
					>
						<AutocompleteInput
							label={translate('form.fields.contact')}
							optionText={contactOptionText}
							inputText={contactInputText}
							helperText={false}
							validate={required()}
							onCreate={() => {}}
						/>
					</ReferenceArrayInput>
					<ReferenceArrayInput
						resource="objects"
						source={INPUT_SOURCE.TAGS}
						reference="tags"
					>
						<SelectArrayInput
							label={translate('form.fields.roles')}
							optionText={({ name, color }) => (
								<Chip
									label={name}
									size="small"
									style={{
										backgroundColor: color,
										border: 0
									}}
								/>
							)}
							helperText={false}
							onCreate={() => {}}
							sx={{
								width: 200,
								minWidth: 200,
								'& .RaSelectArrayInput-chip': {
									backgroundColor: 'transparent'
								}
							}}
						/>
					</ReferenceArrayInput>
				</SimpleFormIterator>
			</ArrayInput>
			<Stack>
				<Typography variant="h6">{translate('form.fields.manager')}</Typography>
				<ReferenceInput
					source={INPUT_SOURCE.SALES_ID}
					reference="sales"
					filter={{
						'disabled@neq': true
					}}
				>
					<SelectInput
						label={translate('form.fields.manager')}
						helperText={false}
						optionText={choice => `${choice.first_name} ${choice.last_name}`}
						validate={required()}
					/>
				</ReferenceInput>
			</Stack>
		</>
	);
};

export const BuildingInputs = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	return (
		<Stack gap={2} p={1}>
			<Stack gap={3} direction={isMobile ? 'column' : 'row'}>
				<Stack gap={3} flex={5}>
					<PlacementSectionInputs />
				</Stack>
				<Divider orientation={isMobile ? 'horizontal' : 'vertical'} flexItem />
				<Stack gap={2} flex={5}>
					<DescriptionSectionInputs />
					<DocumentsSectionInputs />
					{/*<ContactsSectionInputs />*/}
				</Stack>
			</Stack>
		</Stack>
	);
};
